import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import jwtDecode from 'jwt-decode';
import dayjs from 'dayjs';
import { setAuth } from '../utilities/axios';
import { submitToken } from '../containers/Token/tokenSlice';
import { authUserBalance, setTokenValues } from '../containers/Auth/authSlice';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import Loader from '../components/Loader';
import { useSearchParams } from 'react-router-dom';

const TokenContext = createContext();

const TokenContextProvider = ({ children }) => {
	/* ====================== HOOKS ====================== */
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const { error, hasBeenSubmitted, isLoading, token } = useSelector((state) => state.tokenSlice);

	const [encryptedToken, setEncryptedToken] = useState(undefined);
	const [isValidatingToken, setIsValidatingToken] = useState(true);
	const [sessionTimer, setSessionTimer] = useState(0);
	const [sessionExpired, setSessionExpired] = useState(false);
	const [tokenIsValid, setTokenIsValid] = useState(false);
	const [siteId, setSiteId] = useState();

	const handleReturnToSite = () => {
		window.location.replace(siteInfoById[siteId].homeUrl);
	};

	/* ====================== EFFECTS ====================== */
	useEffect(() => {
		const locationString = window.location.search;
		let urlToken = queryString.parse(locationString).token;
		if (urlToken) {
			urlToken = decodeURI(urlToken);
		}
		let localStorageToken = localStorage.getItem('token');
		if (localStorageToken === 'undefined') {
			localStorage.removeItem('token');
			localStorageToken = undefined;
		}

		if (urlToken) {
			localStorage.setItem('token', urlToken);

			const newParams = new URLSearchParams(searchParams);
			newParams.delete('token');

			setSearchParams(newParams);
			setEncryptedToken(urlToken);
		}

		if (!urlToken && localStorageToken) {
			setEncryptedToken(localStorageToken);
		}

		if (!urlToken && !localStorageToken) {
			setIsValidatingToken(false);
			setTokenIsValid(false);
		}
	}, []);

	useEffect(() => {
		if (localStorage.getItem('site_id')) {
			setSiteId(localStorage.getItem('site_id'));
		}
		localStorage.setItem('site_id', siteId);
	}, []);

	useEffect(() => {
		if (!hasBeenSubmitted && encryptedToken) {
			dispatch(submitToken({ token: encryptedToken }));
		}
	}, [encryptedToken]);

	useEffect(() => {
		if (token && hasBeenSubmitted) {
			const parsedToken = jwtDecode(token);
			setAuth(`Bearer ${token}`);

			dispatch(setTokenValues(parsedToken));
			dispatch(authUserBalance({ siteId: parsedToken.site_id, memberNumber: parsedToken.cust_id }));

			setTokenIsValid(true);
			startExpirationTimer();
		}
	}, [hasBeenSubmitted]);

	/* ====================== METHODS ====================== */
	const startExpirationTimer = () => {
		const parsedToken = jwtDecode(token);
		const calculateSessionExpiration = () => {
			setSessionTimer((parsedToken.exp - dayjs() / 1000).toFixed(0));

			if (dayjs() / 1000 >= parsedToken.exp) {
				clearInterval(timer);
				setSessionExpired(true);
				localStorage.removeItem('token');
			}
		};
		const timer = setInterval(calculateSessionExpiration, 1000);
	};

	/* ====================== MEMO ====================== */
	const value = useMemo(
		() => ({
			sessionTimer
		}),
		[sessionTimer]
	);

	/* ====================== RENDER ====================== */
	if (error || sessionExpired) {
		if (error === 'jwt expired' || sessionExpired) {
			localStorage.removeItem('token');
			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						fontFamily: 'Helvetica, serif',
						marginTop: '30px'
					}}
				>
					<div>
						<ErrorOutlineIcon
							sx={{
								verticalAlign: 'middle',
								fontSize: {
									xs: 15,
									md: 19
								},
								marginRight: '3px',
								transform: 'translateY(-1px)'
							}}
						/>
						Your session has expired. Please try again.
					</div>
				</div>
			);
		}
	}

	if ((!hasBeenSubmitted || isLoading) && isValidatingToken) {
		return <Loader />;
	}
	if (!tokenIsValid) {
		return <p>Hello, World.</p>;
	}

	return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>;
};

TokenContextProvider.propTypes = {
	children: PropTypes.object
};

export const TokenProvider = TokenContextProvider;

export const useToken = () => {
	return useContext(TokenContext);
};
